import { Location } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  DeferredRequest,
  NetworkService,
  offlineClient,
} from '@sentry/angular-pwa';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { API_BASE } from '../api-base.token';
import { WORKSPACE_ID } from '../config/known-headers-keys';
import { BlankInspectionRequest } from './blank-inspection-request';
import { InspectionListSummary } from './inspection-list-summary-response';
import { InspectionReportOverviewResponse } from './inspection-report-overview-response';
import { InspectionReportRequest } from './inspection-report-request';
import { InspectionReportResponse } from './inspection-report-response';
import {
  EquipmentAnswersRequest,
  InspectionRequest,
} from './inspection-request';
import { InspectionResponse } from './inspection-response';
import { UpComingInspectionListItemResponse } from './up-coming-inspection-response';
import { InspectionListItemResponse } from './inspection-list-item-response';

const path = 'inspections';

@Injectable({
  providedIn: 'root',
})
export class InspectionsHttpService {
  constructor(
    private readonly httpClient: HttpClient,
    @Inject(API_BASE)
    private readonly apiBase: string,
    private readonly network: NetworkService
  ) {}

  get(
    pageNumber: number,
    pageSize: number,
    inspectorMemberId: string | null = null,
    zoneId: string | null = null,
    status: string | null = null,
    examType: string | null = null,
    workspaceId: string | null = null
  ) {
    let params = new HttpParams();
    params = params.append('pageSize', pageSize);
    params = params.append('page', pageNumber);
    if (inspectorMemberId) {
      params = params.append('inspectorMemberId', inspectorMemberId);
    }
    if (zoneId) {
      params = params.append('zoneId', zoneId);
    }
    if (status) {
      params = params.append('status', status);
    }
    if (examType) {
      params = params.append('examType', examType);
    }

    const url = Location.joinWithSlash(this.apiBase, `${path}`);

    let headers = new HttpHeaders();
    if (workspaceId) {
      headers = headers.set(WORKSPACE_ID, workspaceId);
    }

    return this.httpClient.get<InspectionListSummary>(url, { params, headers });
  }

  getCompletedInspection(inspectionId: string) {
    const url = Location.joinWithSlash(this.apiBase, `${path}/${inspectionId}`);

    return this.httpClient.get<InspectionResponse>(url);
  }

  getReports() {
    const url = Location.joinWithSlash(this.apiBase, `${path}/reports`);

    return this.httpClient.get<InspectionReportResponse[]>(url);
  }

  post(request: InspectionRequest) {
    const url = Location.joinWithSlash(this.apiBase, `${path}`);

    const client = offlineClient(this.httpClient);

    const deferredRequest: DeferredRequest = {
      type: 'POST',
      url,
      body: request,
    };

    return client(deferredRequest, this.network);
  }

  postBlankInspection(request: BlankInspectionRequest) {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/blank-inspection`
    );

    return this.httpClient.post(url, request);
  }

  getUpComing() {
    const url = Location.joinWithSlash(this.apiBase, `${path}/up-coming`);

    return this.httpClient.get<UpComingInspectionListItemResponse[]>(url);
  }

  upload(files: File[]) {
    const formData: FormData = new FormData();

    files.forEach((file) => formData.append('file', file));

    return this.httpClient.post<string[]>(
      `${Location.joinWithSlash(this.apiBase, `${path}`)}/upload`,
      formData
    );
  }

  getReport(request: InspectionReportRequest): Observable<Blob> {
    const url = Location.joinWithSlash(this.apiBase, `${path}/report`);
    const httpOptions = {
      responseType: 'blob' as 'json',
    };

    return this.httpClient.post<Blob>(url, request, httpOptions);
  }

  getReportOverview(): Observable<InspectionReportOverviewResponse> {
    const url = Location.joinWithSlash(this.apiBase, `${path}/report-overview`);

    return this.httpClient.get<InspectionReportOverviewResponse>(url);
  }

  updateEquipmentAnswers(
    inspectionId: string,
    equipmentAnswers: EquipmentAnswersRequest
  ): Observable<void> {
    const url = Location.joinWithSlash(this.apiBase, `${path}/${inspectionId}`);

    return this.httpClient.put<void>(url, equipmentAnswers);
  }

  getInspectionsByShiftOccurrence(
    workspaceId: string,
    shiftOccurrenceId: number
  ): Observable<InspectionListItemResponse[]> {
    const url = Location.joinWithSlash(this.apiBase, path);
    
    let params = new HttpParams();
    params = params.append('shiftOccurrenceId', shiftOccurrenceId.toString());
    params = params.append('pageSize', '100');
    params = params.append('page', '0');
    
    const headers = new HttpHeaders().set(WORKSPACE_ID, workspaceId);
    
    return this.httpClient.get<InspectionListSummary>(url, { params, headers }).pipe(
      map((response) => response.inspections || [])
    );
  }
}
